import i18n from "i18next";

import { getCountryRegionNames, getCountryRegions, updateUserSetting } from "services";

import { getLocalKlimVar, initCookieBanner, isCrowdinInContextSupported, loadCrowdinInContext, setLocalKlimVar, unloadCrowdinInContext } from "utils";

import { COUNTRY_CODE, LANGUAGE_CODE, LOCALE_DICT } from "consts";

import { GroupedRegion, User, hasGroupedRegions } from "models";

export { type TFunction } from "i18next";

export type RegionsData =
  | {
      isGrouped: false;
      regions: string[];
    }
  | {
      isGrouped: true;
      groupedRegions: GroupedRegion[];
    };

const LEGACY_LANGUAGE_CODE_ALIASES: Partial<Record<string, LANGUAGE_CODE>> = {
  hu: LANGUAGE_CODE.HUNGARIAN,
  cs: LANGUAGE_CODE.CZECH,
  sk: LANGUAGE_CODE.SLOVAK,
};

export const normalizeLanguageCode = (code: string | null | undefined): LANGUAGE_CODE => {
  if (!code) return LANGUAGE_CODE.GERMAN;

  // Match case-insensitively so legacy lowercase region codes (e.g. "cs-cz", "en-gb") resolve to the
  // canonical "cs-CZ"/"en-GB" instead of falling through to German. Normal use self-heals the casing
  // via the profile write on changeLanguage, but that write is blocked (403) while impersonating.
  const match = (Object.values(LANGUAGE_CODE) as LANGUAGE_CODE[]).find((value) => value.toLowerCase() === code.toLowerCase());
  return match ?? LEGACY_LANGUAGE_CODE_ALIASES[code] ?? LANGUAGE_CODE.GERMAN;
};

export const getLanguageFromLocalStorage = (forDates?: { forDates?: boolean }) => {
  const lang = normalizeLanguageCode(getLocalKlimVar("language"));

  if (forDates && lang === LANGUAGE_CODE.ZULU) {
    return LANGUAGE_CODE.ENGLISH;
  }

  return lang;
};

export const getLocalStorageCountry = (): COUNTRY_CODE | null => getLocalKlimVar("country");
export const setLocalStorageCountry = (value: COUNTRY_CODE) => setLocalKlimVar("country", value);

const LANGUAGE_BEFORE_ACHOLI_KEY = "languageBeforeAcholi";

// Language to restore when exiting Crowdin in-context - Acholi
// && falls back to profile language or German :D
export const getPreferredLanguageBeforeAcholi = (user?: User | null): LANGUAGE_CODE => {
  const languageBeforeAcholi = getLocalKlimVar(LANGUAGE_BEFORE_ACHOLI_KEY) as LANGUAGE_CODE | null;
  if (languageBeforeAcholi && languageBeforeAcholi !== LANGUAGE_CODE.ACHOLI) {
    return languageBeforeAcholi;
  }
  if (user?.language && user.language !== LANGUAGE_CODE.ACHOLI) {
    return user.language;
  }
  return LANGUAGE_CODE.GERMAN;
};

const persistLanguageBeforeAcholi = (currentLanguage: LANGUAGE_CODE) => {
  if (currentLanguage !== LANGUAGE_CODE.ACHOLI) {
    setLocalKlimVar(LANGUAGE_BEFORE_ACHOLI_KEY, currentLanguage);
  }
};

// Leave Crowdin in-context (Acholi) and restore the user's previous or profile language
export const exitAcholiInContextTranslation = (user: User | null) => {
  const fallbackLanguage = getPreferredLanguageBeforeAcholi(user);
  return changeLanguage(fallbackLanguage, user).then(() => window.location.reload());
};

const loadCrowdinInContextForAcholi = (user: User | null) => {
  // Both the user pressing escape and an unrecoverable Crowdin error exit in-context the same way.
  const exit = () => exitAcholiInContextTranslation(user);
  loadCrowdinInContext({ onEscape: exit, onFailure: exit });
};

export const changeLanguage = (language: LANGUAGE_CODE, user: User | null) => {
  // Backend data can still carry a legacy bare language code (e.g. "hu" instead of "hu-HU") —
  // coerce it to the current LANGUAGE_CODE before it's persisted or synced to the profile.
  const normalizedLanguage = normalizeLanguageCode(language);

  // Acholi only works where Crowdin in-context can run (secure https, non-production).
  // Over local http it would inject Crowdin, fail, and reload — colliding with the
  // cookie-consent reload — so fall back to German there instead.
  const targetLanguage = normalizedLanguage === LANGUAGE_CODE.ACHOLI && !isCrowdinInContextSupported() ? LANGUAGE_CODE.GERMAN : normalizedLanguage;

  if (targetLanguage === LANGUAGE_CODE.ACHOLI) {
    const currentLanguage = (i18n.language || getLanguageFromLocalStorage()) as LANGUAGE_CODE;
    persistLanguageBeforeAcholi(currentLanguage);
  }
  // Store in localStorage before i18n.changeLanguage fires its languageChanged event so that
  // any synchronous re-renders triggered by that event read the correct locale immediately.
  setLocalKlimVar("language", targetLanguage);
  document.documentElement.setAttribute("lang", targetLanguage.toLowerCase());

  return i18n
    .changeLanguage(targetLanguage)
    .then(() => {
      // Acholi drives the Crowdin in-context editor: load on switch-in, tear down on switch-out. (R4, R5)
      if (targetLanguage === LANGUAGE_CODE.ACHOLI) {
        loadCrowdinInContextForAcholi(user);
      } else {
        unloadCrowdinInContext();
      }

      // Acholi stays local-only so it never follows the account to production — never sync the
      // profile when Acholi was requested, even when we fell back to German on http. (R7)
      if (user && normalizedLanguage !== LANGUAGE_CODE.ACHOLI) {
        return updateUserSetting({
          item: "language",
          value: targetLanguage,
        });
      }
    })
    .then(() => {
      // Re-init the cookie banner in the new language. Iubenda only picks up a language change on a page
      // reload, and initCookieBanner triggers that reload when it detects the language changed. It must run
      // AFTER the profile update above — otherwise the reload interrupts that write and the profile-language
      // sync on the next load (see UserContext) reverts the whole app back to the old language.
      initCookieBanner();
    })
    .catch((error) => {
      // eslint-disable-next-line
      console.error(`Changing the language to ${targetLanguage} has failed`, { error });
    });
};

export const setLanguageInLocalStorage = () => {
  const languageFromLocalStorage: LANGUAGE_CODE = getLanguageFromLocalStorage() as LANGUAGE_CODE;

  if (i18n.language !== languageFromLocalStorage) {
    return changeLanguage(languageFromLocalStorage || LANGUAGE_CODE.GERMAN, null);
  }
  // Already on the stored language, so changeLanguage won't fire — but a refresh into
  // Acholi still needs the in-context editor reloaded where supported; otherwise revert
  // to German so local http never re-engages Crowdin. (R4 on bootstrap)
  if (languageFromLocalStorage === LANGUAGE_CODE.ACHOLI) {
    if (isCrowdinInContextSupported()) {
      loadCrowdinInContextForAcholi(null);
    } else {
      return changeLanguage(LANGUAGE_CODE.GERMAN, null);
    }
  }
  return Promise.resolve();
};

// t()'s per-call `lng` override only reads already-loaded resources, so callers that render
// text in a language other than the current UI language must load it first.
export const loadLanguages = (languages: LANGUAGE_CODE[]): Promise<void> =>
  new Promise((resolve) => {
    i18n.loadLanguages(languages, () => resolve());
  });

export const localNumberFormat = () => {
  const language = getLanguageFromLocalStorage();
  const locale = LOCALE_DICT[language] ? LOCALE_DICT[language] : LOCALE_DICT.de;
  return locale;
};

export const getRegionsByCountry = async ({ signal, countryCode }: { signal: AbortSignal; countryCode: COUNTRY_CODE }): Promise<RegionsData> => {
  const response = await getCountryRegionNames({ signal, countryCode });

  if (hasGroupedRegions(response)) {
    // Transform object { "England": [...] } into array [{ country: "England", regions: [...] }]
    const sortedGroupedRegions: GroupedRegion[] = Object.entries(response.groupedRegions)
      .map(([country, regions]) => ({
        country: country as COUNTRY_CODE,
        regions: [...regions].sort((a: string, b: string) => a.localeCompare(b)),
      }))
      .sort((a, b) => a.country.localeCompare(b.country));

    return {
      isGrouped: true,
      groupedRegions: sortedGroupedRegions,
    };
  }

  // Sort flat regions alphabetically other than GB
  const sortedRegions = response.regions ? [...response.regions].sort((a, b) => a.localeCompare(b)) : [];

  return {
    isGrouped: false,
    regions: sortedRegions,
  };
};

export const getShapefileRegions = async ({ signal }: { signal: AbortSignal }) => {
  const response = await getCountryRegions({ signal, countryCode: COUNTRY_CODE.GERMANY });
  const germanRegions = response?.regions;
  // edge case for shape files: not seperated by country regions for Austria, Switzerland and Poland
  const shapeFileRegions = { ...germanRegions, AUT: "Österreich", CH: "Schweiz", PL: "Polen" };
  return shapeFileRegions;
};

const LANGUAGE_CODE_MAP = {
  [LANGUAGE_CODE.GERMAN]: LANGUAGE_CODE.GERMAN,
  [LANGUAGE_CODE.SWISS]: LANGUAGE_CODE.GERMAN,
  [LANGUAGE_CODE.AUSTRIAN]: LANGUAGE_CODE.GERMAN,
  [LANGUAGE_CODE.POLISH]: LANGUAGE_CODE.POLISH,
  [LANGUAGE_CODE.CZECH]: LANGUAGE_CODE.CZECH,
  [LANGUAGE_CODE.SLOVAK]: LANGUAGE_CODE.SLOVAK,
  [LANGUAGE_CODE.HUNGARIAN]: LANGUAGE_CODE.HUNGARIAN,
  [LANGUAGE_CODE.ENGLISH_GB]: LANGUAGE_CODE.ENGLISH_GB,
  [LANGUAGE_CODE.ENGLISH_US]: LANGUAGE_CODE.ENGLISH_US,
  [LANGUAGE_CODE.ENGLISH]: LANGUAGE_CODE.ENGLISH,
  [LANGUAGE_CODE.ZULU]: LANGUAGE_CODE.ENGLISH,
  [LANGUAGE_CODE.ACHOLI]: LANGUAGE_CODE.ENGLISH, // this language is only used for in-context-localization for crowdin
} as const satisfies Record<LANGUAGE_CODE, LANGUAGE_CODE>;

export const getLanguageCode = (langCode: LANGUAGE_CODE) => LANGUAGE_CODE_MAP[langCode] ?? LANGUAGE_CODE.GERMAN;

export const getLanguageCodeFromCountryCode = (countryCode: string) => {
  switch (countryCode.toUpperCase()) {
    case COUNTRY_CODE.GERMANY:
    case COUNTRY_CODE.AUSTRIA:
    case COUNTRY_CODE.SWITZERLAND:
      return LANGUAGE_CODE.GERMAN;
    case COUNTRY_CODE.POLAND:
      return LANGUAGE_CODE.POLISH;
    case COUNTRY_CODE.CZECHIA:
      return LANGUAGE_CODE.CZECH;
    case COUNTRY_CODE.SLOVAKIA:
      return LANGUAGE_CODE.SLOVAK;
    case COUNTRY_CODE.HUNGARY:
      return LANGUAGE_CODE.HUNGARIAN;
    case COUNTRY_CODE.UNITED_KINGDOM:
      return LANGUAGE_CODE.ENGLISH_GB;
    case COUNTRY_CODE.USA:
      return LANGUAGE_CODE.ENGLISH_US;
    default:
      return LANGUAGE_CODE.GERMAN;
  }
};
